import React, { useState } from 'react';
import { ControlledModal } from '@instinet-analytics/semantic-ui-components';
import { useDispatchSwitchLayoutAction } from 'layout/store/dispatcher';
import { useMakeRequest } from 'utils/hooks/request';
import { TextInput } from '@instinet-analytics/semantic-ui-components';
import { useInputError } from '@instinet-analytics/core-lib';
import './CreateLayoutModal.scss';
import { duplicateLayoutAction } from 'layoutManagement/store/thunks';
import { isLayoutNameValid } from 'layoutManagement/layoutNameValidator';
import { Layout } from 'layout/LayoutState';
import {
    isWellKnownLayoutApiErrorResponse,
    WellKnownLayoutApiErrorCodes,
} from 'layout/WellKnownLayoutApiErrorCodes';
import { useDispatch } from 'store/hooks';
import { showWarningAsync } from 'notification/store/thunks';
import { useLayoutsList } from 'layout/store/useLayoutsList';
import { useAppLayoutOrigin } from 'layout/WellKnownLayoutOrigin';

interface SaveLayoutAsModalProps {
    isOpen: boolean;
    activeLayout: Layout;
    onActionCompleted: () => void;
}

export default function SaveLayoutAsModal(props: SaveLayoutAsModalProps) {
    const {
        isOpen,
        onActionCompleted: onActionCompletedProps,
        activeLayout,
    } = props;
    const dispatch = useDispatch();
    const appLayoutOrigin = useAppLayoutOrigin();
    const { forceLayoutListReload } = useLayoutsList(appLayoutOrigin);

    const { id: activeLayoutId, name: activeLayoutName } = activeLayout;

    const [layoutName, setLayoutName] = useState('');

    const makeRequest = useMakeRequest();
    const dispatchSwitchLayout = useDispatchSwitchLayoutAction();

    const {
        errorMessage,
        invalidInput,
        setInputTextAndClearInvalidState,
        clearError,
        displayError,
    } = useInputError();

    const reset = () => {
        clearError();
        setLayoutName('');
    };

    const onActionCompleted = () => {
        reset();
        onActionCompletedProps();
    };

    const createLayout = () => {
        if (!layoutName) {
            setLayoutName('');
            displayError('Please enter a layout name');
            return;
        }

        const layoutNameValidation = isLayoutNameValid(layoutName);
        if (!layoutNameValidation.isValid) {
            displayError(layoutNameValidation.message!);
            return;
        }

        if (layoutName === activeLayoutName) {
            displayError('Please choose a new layout name');
            return;
        }

        makeRequest<[string, string], void>(duplicateLayoutAction)(
            activeLayoutId!.toString(),
            layoutName
        )
            .then(() => {
                dispatchSwitchLayout(activeLayout.origin, layoutName);
                forceLayoutListReload();
                onActionCompleted();
            })
            .catch((err) => {
                if (
                    isWellKnownLayoutApiErrorResponse(
                        err,
                        WellKnownLayoutApiErrorCodes.LayoutNewNameAlreadyExists
                    )
                ) {
                    displayError(
                        `A layout already exists with name '${layoutName}'. Please enter a new name.`
                    );
                } else {
                    const message = err.message || err.data.message;
                    if (message) {
                        dispatch(showWarningAsync(message));
                    }
                    displayError(`Unexpected error saving layout`);
                }
            });
    };

    return (
        <ControlledModal
            className="SaveLayoutAsModal"
            headerText="Save Layout As"
            saveText="Save As"
            isOpen={isOpen}
            onCancel={onActionCompleted}
            onSave={createLayout}
        >
            <div>
                <div className="title">
                    Save Layout &apos;{activeLayoutName}&apos; as:
                </div>
                {errorMessage && <div className="error">{errorMessage}</div>}
                <TextInput
                    value={layoutName}
                    onChange={setInputTextAndClearInvalidState(setLayoutName)}
                    placeholder="Enter layout name"
                    error={invalidInput}
                    maxLength={100}
                    onEnter={createLayout}
                    autoFocus={true}
                />
            </div>
        </ControlledModal>
    );
}
